'use client';

import { useNewsletterForm } from '@/hooks/useNewsletterForm';

interface NewsletterFormProps {
  source: string;
  variant?: 'light' | 'dark';
  size?: 'small' | 'medium' | 'large' | 'custom';
  showLabel?: boolean;
  label?: string;
  placeholder?: string;
  className?: string;
  buttonText?: string;
  onSuccess?: () => void;
}

export default function NewsletterForm({
  source,
  variant = 'light',
  size = 'medium',
  showLabel = false,
  label = 'Get the latest in AI: subscribe to our newsletter.',
  placeholder = 'EMAIL ADDRESS',
  className = '',
  buttonText = 'SUBSCRIBE',
  onSuccess
}: NewsletterFormProps) {
  const {
    email,
    setEmail,
    isLoading,
    isSubscribed,
    message,
    error,
    subscribe,
    reset
  } = useNewsletterForm({
    source,
    onSuccess: () => {
      onSuccess?.();
    }
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await subscribe();
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSubmit(e as any);
    }
  };

  // Size variants
  const sizeClasses = {
    small: {
      input: 'h-8 px-4 text-[12px] tracking-[1.2px]',
      button: 'h-8 px-4 text-[12px] tracking-[1.2px]',
      container: 'gap-2',
      inputWidth: 'w-full'
    },
    medium: {
      input: 'h-10 px-5 text-[13px] sm:text-[14px] tracking-[1.3px] sm:tracking-[1.4px]',
      button: 'h-10 px-5 sm:px-6 text-[13px] sm:text-[14px] tracking-[1.3px] sm:tracking-[1.4px]',
      container: 'gap-3',
      inputWidth: 'w-full'
    },
    large: {
      input: 'h-12 px-6 text-[14px] sm:text-[16px] tracking-[1.4px] sm:tracking-[1.6px]',
      button: 'h-12 px-6 sm:px-8 text-[14px] sm:text-[16px] tracking-[1.4px] sm:tracking-[1.6px]',
      container: 'gap-4',
      inputWidth: 'w-full'
    },
    custom: {
      input: 'h-10 px-4 sm:px-5 lg:px-6 text-[12px] sm:text-[13px] lg:text-[14px] tracking-[1.2px] sm:tracking-[1.3px] lg:tracking-[1.4px]',
      button: 'h-10 px-3 sm:px-4 lg:px-6 text-[11px] sm:text-[12px] lg:text-[14px] tracking-[1.1px] sm:tracking-[1.2px] lg:tracking-[1.4px] w-[25vw] max-w-[120px] sm:w-auto sm:min-w-[100px]',
      container: 'gap-2 sm:gap-3',
      inputWidth: 'flex-1 min-w-0 sm:max-w-[320px] lg:max-w-[380px]'
    }
  };

  // Color variants
  const colorClasses = variant === 'dark' ? {
    label: 'text-white',
    inputBg: 'bg-[rgba(255,255,255,0.1)]',
    inputBorder: 'border-[rgba(255,255,255,0.2)]',
    inputText: 'text-white placeholder:text-[rgba(255,255,255,0.7)]',
    message: 'text-green-300',
    error: 'text-red-300'
  } : {
    label: 'text-black',
    inputBg: 'bg-white',
    inputBorder: 'border-gray-300',
    inputText: 'text-black placeholder:text-gray-500',
    message: 'text-green-600',
    error: 'text-red-600'
  };

  const currentSize = sizeClasses[size];

  // Show success state
  if (isSubscribed && message) {
    return (
      <div className={`${className} ${size === 'custom' ? 'flex items-center gap-4 animate-fadeIn w-full' : 'text-center animate-fadeIn'}`}>
        <div className={`${colorClasses.message} ${size === 'custom' ? 'flex items-center gap-3 w-full justify-center' : 'mb-4'}`}>
          <div className={`flex items-center ${size === 'custom' ? 'gap-2' : 'justify-center gap-2 mb-2'}`}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className={`${variant === 'dark' ? 'text-green-400' : 'text-green-600'} sm:w-5 sm:h-5`}>
              <path d="M9 12L11 14L15 10M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            <span className={`font-ibm-plex-sans-condensed text-[11px] sm:text-[12px] lg:text-[14px] tracking-[1.1px] sm:tracking-[1.2px] lg:tracking-[1.4px] uppercase ${variant === 'dark' ? 'text-white' : 'text-black'} font-medium`}>Thank you for subscribing!</span>
          </div>
          {size !== 'custom' && <p className="font-ibm-plex-sans text-[14px] leading-relaxed">{message}</p>}
        </div>
        {size !== 'custom' && (
          <button
            onClick={reset}
            className="font-ibm-plex-sans-condensed text-[12px] tracking-wider uppercase underline opacity-70 hover:opacity-100 transition-opacity"
          >
            Subscribe Another Email
          </button>
        )}
      </div>
    );
  }

  return (
    <div className={className}>
      {showLabel && label && (
        <div className={`font-ibm-plex-sans font-medium ${colorClasses.label} text-[14px] sm:text-[16px] lg:text-[18px] mb-3 sm:mb-4`}>
          <p className="leading-[20px] sm:leading-[22px] lg:leading-[24px]">{label}</p>
        </div>
      )}

      <form onSubmit={handleSubmit} className={`flex items-center ${currentSize.container} ${size === 'custom' ? 'flex-row w-full' : 'w-full flex-col sm:flex-row'}`}>
        <div className={size === 'custom' ? currentSize.inputWidth : 'flex-1 w-full'}>
          <div className={`${colorClasses.inputBg} ${currentSize.input} relative rounded-none border ${colorClasses.inputBorder} w-full`}>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder={placeholder}
              disabled={isLoading}
              className={`bg-transparent border-none outline-none font-ibm-plex-sans-condensed ${colorClasses.inputText} uppercase w-full h-full`}
              required
            />
          </div>
        </div>

        <button
          type="submit"
          disabled={isLoading}
          className={`bg-[#e7131a] ${currentSize.button} relative rounded-none transition-all duration-200 hover:bg-[#c21015] disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer flex-shrink-0 flex items-center justify-center gap-2`}
        >
          {isLoading && (
            <svg className="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
          )}
          <span className="font-ibm-plex-sans-condensed text-white text-center whitespace-nowrap uppercase">
            {isLoading ? 'SUBSCRIBING...' : buttonText}
          </span>
        </button>
      </form>

      {/* Error message */}
      {error && (
        <div className={`${colorClasses.error} mt-2 text-center`}>
          <p className="font-ibm-plex-sans text-[12px] sm:text-[14px]">{error}</p>
        </div>
      )}
    </div>
  );
}